/**
 * The canvas's `How It Works` screen.
 *
 * Four columns under hairline rules. The first three numerals are outlined via
 * `-webkit-text-stroke` on transparent text; the fourth is filled with the
 * accent and sits under a 2px accent rule instead of a 1px hairline. That
 * asymmetry is the design marking the terminal step, so it survives here rather
 * than being normalised into a uniform loop.
 */

interface Step {
  n: string;
  label: string;
  body: string;
  code: string;
  /** Step 04 fills its numeral and thickens its rule. */
  terminal?: boolean;
}

const STEPS: Step[] = [
  {
    n: "01",
    label: "Request",
    body: "Say it the way you would say it to a person.",
    code: "“Run payroll for March”",
  },
  {
    n: "02",
    label: "Resolve",
    body: "AI2Aim maps the request to the right agent, data, and permissions.",
    code: "resolve(intent) → plan",
  },
  {
    n: "03",
    label: "Execute",
    body: "The agent runs the work inside the scope of its role.",
    code: "execute(plan) → agents",
  },
  {
    n: "04",
    label: "Outcome delivered",
    body: "Results land with a full audit trail, and the memory grows sharper.",
    code: "commit(outcome) → audit_log",
    terminal: true,
  },
];

const codeStyle: React.CSSProperties = {
  fontFamily: "'Manrope', sans-serif",
  fontSize: 12,
  background: "var(--chip)",
  color: "#9DF1FF",
  borderRadius: 9,
  padding: "9px 13px",
  display: "inline-block",
  alignSelf: "flex-start",
};

export default function HowItWorks() {
  return (
    <section
      id="how"
      className="site-gutter"
      style={{
        paddingTop: 122,
        paddingBottom: 116,
        position: "relative",
        overflow: "clip",
      }}
    >
      <div style={{ maxWidth: 1240, margin: "0 auto", position: "relative" }}>
        <div data-rv="" style={{ maxWidth: 640, marginBottom: 60 }}>
          <div
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 11.5,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--link)",
              marginBottom: 18,
            }}
          >
            <span className="dc-mark" aria-hidden="true" /> How It Works
          </div>
          <h2
            style={{
              margin: "0 0 16px",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: "clamp(28px, 3.4vw, 46px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.14,
            }}
          >
            From intent to outcome in four moves
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: 16,
              color: "rgba(var(--ink-rgb),.66)",
            }}
          >
            State what you need in plain language. AI2Aim resolves it against
            your data, runs it, and logs the result.
          </p>
        </div>
        {/* Four across from tablet up, one per row on a phone (global.css,
            T101026002). */}
        <div className="home-how-steps">
          {STEPS.map((s) => (
            <div
              key={s.n}
              data-rv=""
              className="home-how-step"
              style={{
                borderTop: s.terminal
                  ? "2px solid var(--acc2)"
                  : "1px solid rgba(var(--ink-rgb),.25)",
                paddingTop: 22,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <span
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: 34,
                  color: s.terminal ? "var(--acc2)" : "transparent",
                  WebkitTextStroke: s.terminal
                    ? undefined
                    : "1.4px var(--ink)",
                }}
              >
                {s.n}
              </span>
              <span
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 12,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--link)",
                }}
              >
                {s.label}
              </span>
              <p
                style={{
                  margin: 0,
                  fontSize: 14.5,
                  color: "rgba(var(--ink-rgb),.66)",
                  flex: 1,
                }}
              >
                {s.body}
              </p>
              <code style={codeStyle}>{s.code}</code>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
